// Taratura strumenti — MOD 07 (T-245).
// Registrazione tarature degli strumenti di misura + stato scadenze.
// Sorgente: public.instrument_calibrations, con cache e coda locale durevoli. L'export A4 è in Oggi → Export Registri (MOD 07).

import { confirmDialog } from '../../lib/ui/dialogs.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput,
  IonItem, IonLabel, IonModal, IonNote, IonPage, IonSelect, IonSelectOption,
  IonSpinner, IonTextarea, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { addOutline, warningOutline } from 'ionicons/icons';
import { useMemo, useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOfflineRegister, offlineRegisterStatus } from '../../lib/haccp/useOfflineRegister.js';

interface CalibrationRow {
  id: string;
  strumento: string;
  tipo_strumento: string;
  matricola: string | null;
  data_taratura: string;
  ente_taratura: string | null;
  tecnico: string | null;
  metodo: string | null;
  esito: string;
  scostamento_rilevato: string | null;
  prossima_scadenza: string | null;
  note: string | null;
}

const TIPO_OPTS: Array<[string, string]> = [
  ['termometro_sonda', 'Termometro a sonda'],
  ['termometro_infrarossi', 'Termometro a infrarossi'],
  ['termometro_ambiente', 'Termometro ambiente'],
  ['sonda_bluetooth', 'Sonda Bluetooth'],
  ['bilancia', 'Bilancia'],
  ['ph_metro', 'pH-metro'],
  ['timer', 'Timer'],
  ['altro', 'Altro'],
];
const METODO_OPTS: Array<[string, string]> = [
  ['verifica_ghiaccio_fondente', 'Verifica ghiaccio fondente (0 °C)'],
  ['verifica_acqua_bollente', 'Verifica acqua bollente (100 °C)'],
  ['confronto_campione', 'Confronto con campione'],
  ['certificato_accredia', 'Certificato ACCREDIA'],
  ['interna', 'Verifica interna'],
  ['altro', 'Altro'],
];
const ESITO_OPTS: Array<[string, string]> = [
  ['conforme', 'Conforme'],
  ['conforme_con_correzione', 'Conforme con correzione'],
  ['non_conforme', 'Non conforme'],
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return y && m && dd ? `${dd}/${m}/${y}` : String(d);
}
function statoScadenza(scad: string | null): { label: string; color: string } {
  if (!scad) return { label: 'Senza scadenza', color: '#94a3b8' };
  const days = Math.floor((new Date(scad + 'T00:00:00Z').getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: 'Scaduta', color: '#dc2626' };
  if (days <= 30) return { label: `In scadenza (${days}gg)`, color: '#ea7300' };
  return { label: 'Valida', color: '#16a34a' };
}
function labelOf(opts: Array<[string, string]>, v: string): string {
  return opts.find(([k]) => k === v)?.[1] ?? v;
}

const EMPTY = {
  strumento: '', tipo_strumento: 'termometro_sonda', matricola: '',
  data_taratura: todayIso(), ente_taratura: '', tecnico: '', metodo: '',
  esito: 'conforme', scostamento_rilevato: '', prossima_scadenza: '', note: '',
};

export function CalibrationsPage(): JSX.Element {
  const { profile } = useAuth();
  const canEdit = profile?.is_platform_owner === true
    || profile?.role === 'consulente' || profile?.role === 'ristoratore' || profile?.role === 'osa';

  const [toast, setToast] = useState<string | null>(null);

  const { register, form, setForm, open, start, close, save: saveRecord, saving, ready,
    hasDraft, hasDraftChanges, discard: discardRecord, draftStatus, draftError, retryDraft } = useOfflineRegister<CalibrationRow, typeof EMPTY>(
    'instrument_calibrations', EMPTY, 'data_taratura', canEdit,
  );
  const { data: rows = [], isLoading: loading, refetch: load } = register;

  const expiringCount = useMemo(
    () => rows.filter((r) => {
      const s = statoScadenza(r.prossima_scadenza);
      return s.label === 'Scaduta' || s.label.startsWith('In scadenza');
    }).length,
    [rows],
  );

  async function discard(): Promise<void> {
    const remove = async () => {
      try { await discardRecord(); }
      catch (error) { setToast('Impossibile scartare la bozza: ' + toUserMessage(error)); }
    };
    if (!hasDraftChanges) { await remove(); return; }
    if (await confirmDialog('I dati della bozza saranno eliminati da questo dispositivo.', {
      header: 'Scartare la bozza?', okText: 'Scarta bozza', danger: true,
    })) await remove();
  }

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (!form.strumento.trim()) { setToast('Inserisci il nome dello strumento'); return; }
    if (!form.data_taratura) { setToast('Inserisci la data di taratura'); return; }
    try {
      const saved = await saveRecord({
        strumento: form.strumento.trim(),
        tipo_strumento: form.tipo_strumento,
        matricola: form.matricola.trim() || null,
        data_taratura: form.data_taratura,
        ente_taratura: form.ente_taratura.trim() || null,
        tecnico: form.tecnico.trim() || null,
        metodo: form.metodo || null,
        esito: form.esito,
        scostamento_rilevato: form.scostamento_rilevato.trim() || null,
        prossima_scadenza: form.prossima_scadenza || null,
        note: form.note.trim() || null,
      });
      if (saved) setToast('Salvato sul dispositivo. In attesa di sincronizzazione.');
    } catch (error) {
      setToast('Salvataggio non riuscito: ' + toUserMessage(error));
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Taratura strumenti</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
          Le registrazioni si salvano sul dispositivo, anche senza rete, e si sincronizzano quando la connessione è disponibile.
        </p>
        {draftError && !open && <div role="alert"><p>{draftError}</p><IonButton onClick={retryDraft}>Riprova bozza</IonButton></div>}
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Registro MOD 07: tarature di termometri, sonde e bilance con stato delle scadenze.
          L'export A4 è in <b>Oggi → Export Registri</b>.
        </p>

        {expiringCount > 0 && (
          <IonItem color="warning" lines="none" style={{ borderRadius: 10, marginBottom: 8 }}>
            <IonIcon icon={warningOutline} slot="start" />
            <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
              {expiringCount} {expiringCount === 1 ? 'strumento da ritarare' : 'strumenti da ritarare'} (scaduti o in scadenza entro 30 giorni).
            </IonLabel>
          </IonItem>
        )}

        {canEdit && (
          <IonButton expand="block" disabled={!ready || saving} onClick={start} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> {hasDraft ? 'Riprendi bozza' : 'Aggiungi taratura'}
          </IonButton>
        )}

        {register.isError && rows.length > 0 && (
          <div role="status"><p>Lettura non riuscita: sono mostrati gli ultimi dati caricati.</p><IonButton onClick={() => { void load(); }}>Riprova</IonButton></div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
        ) : (register.isPending || register.isError) && rows.length === 0 ? (
          <div role="status" style={{ padding: 16 }}>
            <p>Impossibile leggere il registro sul dispositivo. Riprova.</p>
            <IonButton onClick={() => { void load(); }}>Riprova</IonButton>
          </div>
        ) : rows.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>
            Nessuna taratura disponibile su questo dispositivo.
          </p>
        ) : (
          rows.map((r) => {
            const st = statoScadenza(r.prossima_scadenza);
            return (
              <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {offlineRegisterStatus(r) && <p role="status" style={{ fontSize: 12, color: r._syncStatus === 'rejected' ? '#b91c1c' : '#92400e', margin: '0 0 6px' }}>{offlineRegisterStatus(r)}</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <b style={{ fontSize: 15 }}>{r.strumento}</b>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: st.color, padding: '2px 8px', borderRadius: 999 }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  {labelOf(TIPO_OPTS, r.tipo_strumento)}{r.matricola ? ` · matr. ${r.matricola}` : ''}
                </div>
                <div style={{ fontSize: 12, color: '#334155', marginTop: 4 }}>
                  Taratura {fmtDate(r.data_taratura)} · {labelOf(ESITO_OPTS, r.esito)}
                  {r.scostamento_rilevato ? ` · scost. ${r.scostamento_rilevato}` : ''}
                </div>
                <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
                  Prossima scadenza: <b>{fmtDate(r.prossima_scadenza)}</b>{r.ente_taratura ? ` · ${r.ente_taratura}` : ''}
                </div>
              </div>
            );
          })
        )}

        <IonModal isOpen={open} canDismiss={!saving} onDidDismiss={close}>
          <IonHeader><IonToolbar>
            <IonTitle>Nuova taratura</IonTitle>
            <IonButtons slot="end">
              <IonButton color="danger" disabled={saving || !ready} onClick={() => { void discard(); }}>Scarta bozza</IonButton>
              <IonButton disabled={saving} onClick={close}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
              {draftStatus}
            </p>
            {draftError && <div role="alert"><p>{draftError}</p><IonButton disabled={saving} onClick={retryDraft}>Riprova bozza</IonButton></div>}
            <IonItem>
              <IonInput label="Strumento *" labelPlacement="stacked" placeholder="Es. Termometro a sonda cucina"
                value={form.strumento} onIonInput={(e) => setForm((f) => ({ ...f, strumento: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonSelect label="Tipo *" labelPlacement="stacked" value={form.tipo_strumento}
                onIonChange={(e) => setForm((f) => ({ ...f, tipo_strumento: String(e.detail.value) }))}>
                {TIPO_OPTS.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonInput label="Matricola / n. serie" labelPlacement="stacked"
                value={form.matricola} onIonInput={(e) => setForm((f) => ({ ...f, matricola: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonInput type="date" label="Data taratura *" labelPlacement="stacked"
                value={form.data_taratura} onIonInput={(e) => setForm((f) => ({ ...f, data_taratura: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonSelect label="Metodo" labelPlacement="stacked" value={form.metodo} placeholder="Seleziona"
                onIonChange={(e) => setForm((f) => ({ ...f, metodo: String(e.detail.value) }))}>
                {METODO_OPTS.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonInput label="Ente / laboratorio / ditta" labelPlacement="stacked"
                value={form.ente_taratura} onIonInput={(e) => setForm((f) => ({ ...f, ente_taratura: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonInput label="Tecnico" labelPlacement="stacked"
                value={form.tecnico} onIonInput={(e) => setForm((f) => ({ ...f, tecnico: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonSelect label="Esito *" labelPlacement="stacked" value={form.esito}
                onIonChange={(e) => setForm((f) => ({ ...f, esito: String(e.detail.value) }))}>
                {ESITO_OPTS.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonInput label="Scostamento rilevato" labelPlacement="stacked" placeholder="Es. +0,3 °C"
                value={form.scostamento_rilevato} onIonInput={(e) => setForm((f) => ({ ...f, scostamento_rilevato: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonItem>
              <IonInput type="date" label="Prossima scadenza" labelPlacement="stacked"
                value={form.prossima_scadenza} onIonInput={(e) => setForm((f) => ({ ...f, prossima_scadenza: String(e.detail.value ?? '') }))} />
              <IonNote slot="helper">Tipicamente 12 mesi dalla taratura.</IonNote>
            </IonItem>
            <IonItem>
              <IonTextarea label="Note" labelPlacement="stacked" autoGrow
                value={form.note} onIonInput={(e) => setForm((f) => ({ ...f, note: String(e.detail.value ?? '') }))} />
            </IonItem>
            <IonButton expand="block" disabled={saving || !ready} onClick={() => { void save(); }} style={{ marginTop: 16, '--border-radius': '12px' } as React.CSSProperties}>
              {saving ? <IonSpinner name="dots" /> : 'Salva taratura'}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
